/**
 * Image Hotspot Content Type Schema
 * Listen & Color: objects, color palette, zones - uses Color Drop Zone element
 */

import type { ContentConfig, LayoutConfig } from '@ai-content-hub/content-core';
import {
    type PageElementData,
    type ColorDropZoneElementData,
    defaultPageData,
} from '@ai-content-hub/content-elements';

/**
 * Player view layout: how interactive and supplementary content are displayed
 * - tabs: Two tabs - switch between image and content
 * - stacked: Image on top, content below (default)
 * - split: Side-by-side, image left and content right
 */
export type PlayerViewLayout = 'tabs' | 'stacked' | 'split';

/**
 * Layout configuration for Image Hotspot
 */
export interface ImageHotspotLayoutConfig extends LayoutConfig {
    /** Player view layout */
    playerView?: PlayerViewLayout;
    /** Whether to stack panels vertically on mobile */
    responsive?: boolean;
}

/**
 * Image Hotspot Config
 */
export interface ImageHotspotConfig extends Omit<ContentConfig, 'layout'> {
    layout?: ImageHotspotLayoutConfig;
}

/**
 * Image Hotspot Data (Listen & Color: objects + color palette + zones)
 */
export interface ImageHotspotData {
    /** Optional title */
    title?: string;
    /** Optional description / instructions */
    description?: string;
    /** Color Drop Zone: objects, color palette, zones */
    colorDropZoneData: ColorDropZoneElementData;
    /** Optional supplementary content (e.g. lesson, audio/transcript) */
    supplementaryContent?: PageElementData;
}

/**
 * Image Hotspot Content - complete content item
 */
export interface ImageHotspotContent {
    id: string;
    type: 'image-hotspot';
    version: string;
    data: ImageHotspotData;
    config?: ImageHotspotConfig;
}

/**
 * Default i18n translations
 */
export const defaultImageHotspotI18n = {
    en: {
        imageTab: 'Image',
        contentTab: 'Content',
        questionsTab: 'Questions',
        configTab: 'Configuration',
        titleLabel: 'Title',
        titlePlaceholder: 'Enter title',
        descLabel: 'Instructions (Optional)',
        descPlaceholder: 'Enter instructions...',
        zonesErrorMsg: 'You must draw at least 1 hotspot on the image.',
    },
    vi: {
        imageTab: 'Hình ảnh',
        contentTab: 'Nội dung',
        questionsTab: 'Câu hỏi',
        configTab: 'Cấu hình',
        titleLabel: 'Tiêu đề',
        titlePlaceholder: 'Nhập tiêu đề',
        descLabel: 'Hướng dẫn (Tuỳ chọn)',
        descPlaceholder: 'Nhập hướng dẫn...',
        zonesErrorMsg: 'Phải vẽ ít nhất 01 vùng nhận diện (Hotspot) trên ảnh.',
    },
};

/**
 * Default config
 */
export const defaultImageHotspotConfig: ImageHotspotConfig = {
    layout: {
        type: 'horizontal',
        playerView: 'stacked',
        responsive: true,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultImageHotspotI18n,
    },
};

/**
 * Default data (Color Drop Zone: objects + color palette + zones)
 */
export const defaultImageHotspotData: ImageHotspotData = {
    title: '',
    description: '',
    colorDropZoneData: {
        background: { type: 'image', src: '', alt: '', size: 'full', alignment: 'center' },
        zones: [],
        colorPalette: [
            { id: 'red', label: 'Red', value: '#EF4444' },
            { id: 'blue', label: 'Blue', value: '#3B82F6' },
            { id: 'green', label: 'Green', value: '#22C55E' },
        ],
        objects: [],
        allowedObjectTypes: ['image'],
        shuffleObjects: true,
    },
    supplementaryContent: defaultPageData,
};

/**
 * Schema definition
 */
export const imageHotspotSchema = {
    type: 'image-hotspot',
    title: 'Image Hotspot',
    description: 'Image with clickable hotspots that reveal content when clicked.',
    version: '1.0.0',
    category: 'interactive',
    icon: 'MapPin',
    tags: ['image', 'hotspot', 'interactive', 'info'],
    defaultData: defaultImageHotspotData,
    defaultConfig: defaultImageHotspotConfig,
};
